import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Text,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonConfirmDialog,
  OktonDataGrid,
  OktonDataGridColumn,
  OktonMetricCard,
  OktonPageHeader,
  useOktonAuth,
  OktonErrorState,
  OktonInput,
  OktonSelect,
  formatDateTimeSeconds as formatDate,
} from "@okton/platform-ui";

interface PlatformSession {
  id: string;
  sessionId: string;
  subjectId: string;
  applicationId?: string;
  applicationIds?: string[];
  userName?: string;
  userEmail?: string;
  companyId?: string;
  companyName?: string;
  branchId?: string;
  branchName?: string;
  companyMemberships?: Array<{
    companyId: string;
    companyName?: string;
    branchIds?: string[];
  }>;
  contextSource?: "session" | "keycloak-membership";
  issuedAt?: string;
  expiresAt?: string;
  lastSeenAt?: string;
  endedAt?: string;
  endReason?: string;
  revokedBy?: string;
  ipAddress?: string;
  active: boolean;
  source?: string;
}

function formatEndReason(reason?: string): string {
  switch (reason) {
    case "expired":
      return "Expirada";
    case "logout":
      return "Logout";
    case "admin_revoke":
      return "Encerrada pelo administrador";
    case "admin_revoke_all":
      return "Encerrada em massa";
    case "legacy_revoked":
      return "Encerramento legado";
    case "revoked":
      return "Revogada";
    default:
      return reason || "Finalizada";
  }
}

function endReasonColor(reason?: string): string {
  if (reason === "admin_revoke" || reason === "admin_revoke_all") return "red";
  if (reason === "logout") return "blue";
  if (reason === "legacy_revoked") return "orange";
  return "gray";
}

export const SessionsPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [sessions, setSessions] = React.useState<PlatformSession[]>([]);
  const [companies, setCompanies] = React.useState<
    Array<{ id: string; name: string }>
  >([]);
  const [status, setStatus] = React.useState<"active" | "ended">("active");
  const [company, setCompany] = React.useState("");
  const [userFilter, setUserFilter] = React.useState("");
  const [application, setApplication] = React.useState("");
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [exporting, setExporting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirmSession, setConfirmSession] =
    React.useState<PlatformSession | null>(null);
  const [confirmAll, setConfirmAll] = React.useState(false);
  const [confirmAllText, setConfirmAllText] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });

  const load = React.useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        limit: "50",
        status,
        page: String(page),
      });
      if (company) params.set("company", company);
      if (userFilter) params.set("user", userFilter);
      if (application) params.set("application", application);
      if (from) params.set("from", from);
      if (to) params.set("to", to);

      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/sessions?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        },
      );

      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível carregar as sessões.",
        );
      setSessions(payload.data || []);
      setCompanies(payload.companies || []);
      setPagination(
        payload.pagination || { page: 1, limit: 50, total: 0, pages: 1 },
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar sessões.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, status, company, userFilter, application, from, to, page]);

  React.useEffect(() => {
    load();
  }, [load]);
  React.useEffect(() => {
    setPage(1);
  }, [status, company, userFilter, application, from, to]);

  const connectedUsers = new Set(sessions.map((item) => item.subjectId)).size;
  const applications = new Set(
    sessions.flatMap(
      (item) =>
        item.applicationIds || (item.applicationId ? [item.applicationId] : []),
    ),
  ).size;

  const clearFilters = (): void => {
    setCompany("");
    setUserFilter("");
    setApplication("");
    setFrom("");
    setTo("");
  };

  const exportCsv = async (): Promise<void> => {
    if (!accessToken || exporting) return;
    setExporting(true);
    setError("");

    try {
      const params = new URLSearchParams({ status });
      if (company) params.set("company", company);
      if (userFilter) params.set("user", userFilter);
      if (application) params.set("application", application);
      if (from) params.set("from", from);
      if (to) params.set("to", to);

      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/sessions/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Não foi possível exportar as sessões.");
      }

      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `sessoes-sso-${status}.csv`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao exportar sessões.",
      );
    } finally {
      setExporting(false);
    }
  };

  const executeRevoke = async (session: PlatformSession): Promise<void> => {
    if (!accessToken) return;
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/sessions/${session.id}/revoke`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível encerrar a sessão.");
      return;
    }
    setConfirmSession(null);
    await load();
  };

  const executeRevokeAll = async (): Promise<void> => {
    if (!accessToken || confirmAllText !== "ENCERRAR TODAS") return;
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/sessions/revoke-all`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível encerrar as sessões.");
      return;
    }
    setConfirmAll(false);
    setConfirmAllText("");
    await load();
  };

  const sessionColumns: OktonDataGridColumn<PlatformSession>[] = [
    {
      key: "user",
      header: "Usuário",
      render: (session) => (
        <Box>
          <Text fontWeight="600">
            {session.userName || session.userEmail || session.subjectId}
          </Text>
          {session.userEmail ? (
            <Text fontSize="xs" color="gray.500">
              {session.userEmail}
            </Text>
          ) : null}
        </Box>
      ),
    },
    {
      key: "application",
      header: "Cliente SSO",
      value: (session) =>
        (session.applicationIds || []).join(", ") ||
        session.applicationId ||
        "-",
    },
    {
      key: "company",
      header: "Empresa / Filial",
      render: (session) =>
        session.companyId ? (
          <Box>
            <Text fontSize="sm">
              {session.companyId} - {session.companyName || "Empresa"}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Filial {session.branchId || "-"}
            </Text>
          </Box>
        ) : (
          <Text color="gray.500">Sem contexto local</Text>
        ),
    },
    {
      key: "issued",
      header: "Início",
      render: (session) => (
        <Text whiteSpace="nowrap">{formatDate(session.issuedAt)}</Text>
      ),
    },
    {
      key: "lastSeen",
      header: "Último acesso",
      render: (session) => (
        <Text whiteSpace="nowrap">{formatDate(session.lastSeenAt)}</Text>
      ),
    },
    {
      key: "ended",
      header: status === "active" ? "Expira em" : "Finalizada em",
      render: (session) => (
        <Text whiteSpace="nowrap">
          {formatDate(
            status === "active" ? session.expiresAt : session.endedAt,
          )}
        </Text>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (session) => (
        <Badge
          colorScheme={
            session.active ? "green" : endReasonColor(session.endReason)
          }
        >
          {session.active ? "Ativa" : formatEndReason(session.endReason)}
        </Badge>
      ),
    },
    ...(status === "ended"
      ? [
          {
            key: "revokedBy",
            header: "Responsável",
            value: (session: PlatformSession) => session.revokedBy || "-",
          },
        ]
      : [
          {
            key: "actions",
            header: "Ações",
            render: (session: PlatformSession) => (
              <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                onClick={() => setConfirmSession(session)}
              >
                Encerrar sessão
              </Button>
            ),
          },
        ]),
  ];

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Sessões"
        subtitle="Histórico completo das sessões registradas pelo SSO central."
        actions={
          <Flex gap="2" wrap="wrap">
            {status === "active" && (
              <Button
                colorScheme="red"
                variant="outline"
                onClick={() => setConfirmAll(true)}
              >
                Encerrar todas as sessões
              </Button>
            )}
            <Button variant="outline" onClick={exportCsv} isLoading={exporting}>
              Exportar CSV
            </Button>
            <Button variant="outline" onClick={load} isLoading={loading}>
              Atualizar
            </Button>
          </Flex>
        }
      />

      {confirmAll && status === "active" && (
        <Box
          mb="5"
          borderWidth="1px"
          borderColor="red.300"
          bg="red.50"
          rounded="lg"
          p="4"
        >
          <Text fontWeight="700" color="red.700">
            CUIDADO: esta ação encerrará TODAS as sessões ativas do SSO.
          </Text>
          <Text fontSize="sm" color="red.700" mt="1">
            Todos os usuários conectados poderão perder o acesso. Digite
            ENCERRAR TODAS para confirmar.
          </Text>
          <Flex gap="2" mt="3" align="center" wrap="wrap">
            <OktonInput
              name="session-revoke-all-confirmation"
              maxW="260px"
              bg="white"
              value={confirmAllText}
              onChange={(e) => setConfirmAllText(e.target.value)}
              placeholder="ENCERRAR TODAS"
            />
            <Button
              colorScheme="red"
              isDisabled={confirmAllText !== "ENCERRAR TODAS"}
              onClick={executeRevokeAll}
            >
              Confirmar encerramento geral
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setConfirmAll(false);
                setConfirmAllText("");
              }}
            >
              Cancelar
            </Button>
          </Flex>
        </Box>
      )}

      <OktonConfirmDialog
        isOpen={Boolean(confirmSession)}
        onClose={() => setConfirmSession(null)}
        onConfirm={async () => {
          if (confirmSession) await executeRevoke(confirmSession);
        }}
        title="Encerrar sessão"
        description={
          confirmSession
            ? "Confirma o encerramento da sessão deste usuário? O usuário perderá esta sessão ativa."
            : ""
        }
        confirmLabel="Encerrar sessão"
        cancelLabel="Cancelar"
        intent="danger"
      />

      <Tabs
        index={status === "active" ? 0 : 1}
        onChange={(index) => setStatus(index === 0 ? "active" : "ended")}
      >
        <TabList mb="5">
          <Tab>Sessões ativas</Tab>
          <Tab>Sessões finalizadas</Tab>
        </TabList>

        <SimpleGrid columns={[1, 2, 3, 6]} spacing="3" mb="4">
          <OktonSelect
            name="session-company"
            placeholder="Todas as empresas"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          >
            {companies.map((item) => (
              <option key={item.id} value={item.id}>
                {item.id} - {item.name}
              </option>
            ))}
          </OktonSelect>
          <OktonInput
            name="session-user"
            placeholder="Usuário ou e-mail"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
          />
          <OktonInput
            name="session-application"
            placeholder="Sistema / client_id"
            value={application}
            onChange={(e) => setApplication(e.target.value)}
          />
          <OktonInput
            name="session-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <OktonInput
            name="session-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <Button variant="outline" onClick={clearFilters}>
            Limpar filtros
          </Button>
        </SimpleGrid>

        <SimpleGrid columns={[1, 3]} spacing="4" mb="6">
          <OktonMetricCard
            label={
              status === "active" ? "Sessões ativas" : "Sessões finalizadas"
            }
            value={pagination.total}
          />
          <OktonMetricCard label="Usuários" value={connectedUsers} />
          <OktonMetricCard label="Clientes SSO" value={applications} />
        </SimpleGrid>

        <TabPanels>
          {[0, 1].map((index) => (
            <TabPanel key={index} p="0">
              {error ? (
                <OktonErrorState
                  title="Não foi possível carregar as sessões"
                  description={error}
                  minH="180px"
                />
              ) : (
                <OktonDataGrid
                  rows={sessions}
                  columns={sessionColumns}
                  getRowId={(session) => session.id}
                  loading={loading}
                  emptyMessage="Nenhuma sessão encontrada. Ajuste os filtros para consultar outros registros."
                  hidePagination
                />
              )}
              <Flex
                mt="3"
                justify="space-between"
                align="center"
                gap="3"
                wrap="wrap"
              >
                <Text fontSize="sm" color="gray.500">
                  Página {pagination.page} de {pagination.pages} •{" "}
                  {pagination.total} registro(s)
                </Text>
                <Flex gap="2">
                  <Button
                    size="sm"
                    variant="outline"
                    isDisabled={pagination.page <= 1 || loading}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    isDisabled={pagination.page >= pagination.pages || loading}
                    onClick={() =>
                      setPage((value) => Math.min(pagination.pages, value + 1))
                    }
                  >
                    Próxima
                  </Button>
                </Flex>
              </Flex>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
